import { useContext, useEffect, useMemo } from "react";
import {
  ConversationContext,
  ConversationDetailsContext,
  useTextReply
} from "../../context";
import { ConversationTypeEnum } from "@coxauto-ui/cxm/communications/interfaces";
import {
  SelectInput,
  SelectInputEventValue
} from "@interstate/components/SelectInput";
import {
  conversationChannelSelectorId,
  conversationTextFromNumberSelectInputId
} from "../../testIds";
import { InterstateOnChangeEvent } from "@interstate/components/InterstateEvents";
import {
  getFromChannelValueSelectInputOptions,
  getToChannelValueSelectInputOptions,
  useComponentWidth
} from "@coxauto-ui/cxm/communications/util";
import styled from "styled-components";
import classNames from "classnames";

export interface TextChannelValueSelectorProps {
  isDisabled: boolean;
}

const StyledChannelValueSelector = styled.div`
  overflow: hidden;
  width: 100%;

  &.large {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;

    &.side-by-side {
      > div {
        flex: 1;
        overflow: hidden;
      }
    }
  }
`;

export function TextChannelValueSelector({
  isDisabled
}: TextChannelValueSelectorProps) {
  const [width, ref] = useComponentWidth();
  // ✅ Use text-specific context hook
  const {
    selectedToChannelValue,
    setSelectedToChannelValue,
    selectedDealerPhoneInformation,
    setSelectedDealerPhoneInformation,
    isPhoneValidationBlocking
  } = useTextReply();

  const { recentContactText } = useContext(ConversationDetailsContext);
  const { contactDetails, dealerCommunicationDetails } =
    useContext(ConversationContext);

  useEffect(() => {
    if (recentContactText && recentContactText !== selectedToChannelValue) {
      setSelectedToChannelValue(recentContactText);
    }
    // Note: selectedToChannelValue intentionally NOT in dependencies to avoid
    // overriding user selections when they change the dropdown value
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recentContactText, setSelectedToChannelValue]);

  useEffect(() => {
    if (
      selectedDealerPhoneInformation.phoneNumber === "" &&
      dealerCommunicationDetails !== null
    ) {
      // Prefer the first phone that can proceed; fall back to dealer phone
      // (which may still be blocked, but at least shows the right entry).
      const validUserPhone = dealerCommunicationDetails.userPhoneNumbers.find(
        p => p.phoneNumber && p.canProceed
      );
      const defaultNumber =
        validUserPhone?.phoneNumber ||
        dealerCommunicationDetails.dealerPhoneNumber.phoneNumber;

      setSelectedDealerPhoneInformation(prev => ({
        ...prev,
        phoneNumber: defaultNumber
      }));
    }
  }, [
    dealerCommunicationDetails,
    selectedDealerPhoneInformation.phoneNumber,
    setSelectedDealerPhoneInformation
  ]);

  const toChannelOptions = useMemo(
    () =>
      getToChannelValueSelectInputOptions(
        contactDetails,
        ConversationTypeEnum.TEXT
      ),
    [contactDetails]
  );

  const fromChannelOptions = useMemo(
    () => getFromChannelValueSelectInputOptions(dealerCommunicationDetails),
    [dealerCommunicationDetails]
  );

  const onChannelSelection = (
    e: InterstateOnChangeEvent<SelectInputEventValue>
  ) => {
    const value = e.target.value?.toString();
    if (value === undefined) return;
    if (value !== selectedToChannelValue) {
      setSelectedToChannelValue(value);
    }
  };

  const onFromTextingPhoneNumberSelection = (
    e: InterstateOnChangeEvent<SelectInputEventValue>
  ) => {
    setSelectedDealerPhoneInformation(prev => ({
      ...prev,
      phoneNumber: e.target.value || ""
    }));
  };

  return (
    <StyledChannelValueSelector
      className={classNames({
        "side-by-side": true,
        large: width > 600
      })}
      ref={ref}
    >
      <SelectInput
        displayLabel
        data-testid={conversationTextFromNumberSelectInputId}
        disabled={isDisabled}
        displayDeselectOption={false}
        id="conversation-text-from-number-select-input"
        label="Sending Phone"
        name="ConversationTextFromNumberSelectInput"
        options={fromChannelOptions}
        value={selectedDealerPhoneInformation.phoneNumber}
        onChange={onFromTextingPhoneNumberSelection}
      />
      <SelectInput
        displayLabel
        data-testid={conversationChannelSelectorId}
        disabled={isDisabled || isPhoneValidationBlocking}
        displayDeselectOption={false}
        id="conversation-channel-pane-dropdown"
        label="Customer Phone"
        name="ConversationChannelValueDropdown"
        options={toChannelOptions}
        value={selectedToChannelValue}
        onChange={onChannelSelection}
      />
    </StyledChannelValueSelector>
  );
}

export default TextChannelValueSelector;
